import { z } from 'zod';

// Support categories — keep the list short so triage isn't paralysed.
// `engagement` covers questions about an open brief/proposal/invoice;
// `account` is sign-in / portal-access; `billing` is invoice / payment;
// `technical` is portal bugs; `other` is the catch-all.
export const SUPPORT_CATEGORIES = ['account', 'billing', 'engagement', 'technical', 'other'] as const;
export const SupportCategorySchema = z.enum(SUPPORT_CATEGORIES);
export type SupportCategory = z.infer<typeof SupportCategorySchema>;

// Status lifecycle (support-cx Phase A):
//   open → acknowledged → in_progress → awaiting_client ↔ in_progress → resolved
// `closed` stays in the enum for legacy seed rows; the MVP state machine never writes it.
export const SUPPORT_TICKET_STATUSES = [
  'open',
  'acknowledged',
  'in_progress',
  'awaiting_client',
  'resolved',
  'closed',
] as const;
export const SupportTicketStatusSchema = z.enum(SUPPORT_TICKET_STATUSES);
export type SupportTicketStatus = z.infer<typeof SupportTicketStatusSchema>;

// Client → server when a portal user opens a ticket. Auth context supplies
// client_id + user_id server-side; the form posts only the user-supplied fields.
export const SupportTicketInputSchema = z.object({
  category: SupportCategorySchema,
  subject: z.string().min(3, 'A short subject is required').max(200),
  body: z.string().min(10, 'Tell us a little more — at least 10 characters').max(5000),
  // Optional ref a client can quote (e.g. "INV-2049" or "OMG-2055") to scope
  // the ticket to a specific resource. Free-form string; server doesn't resolve.
  related_ref: z.string().max(60).optional(),
});
export type SupportTicketInput = z.infer<typeof SupportTicketInputSchema>;

export const SupportTicketDtoSchema = z.object({
  id: z.string(),                 // SUP-XXXX
  client_id: z.string(),
  user_id: z.string(),
  category: SupportCategorySchema,
  subject: z.string(),
  body: z.string(),
  related_ref: z.string().nullable().optional(),
  status: SupportTicketStatusSchema,
  // Owner triage (Phase A) — staff user who has claimed the ticket. Null while
  // the ticket is awaiting triage. owner_name / owner_role are denormalised
  // for UI rendering; the server joins them from UserModel at read time.
  owner_user_id: z.string().nullable(),
  owner_name: z.string().nullable(),
  owner_role: z.string().nullable(),
  acknowledged_at: z.string().nullable(),
  last_staff_reply_at: z.string().nullable(),
  last_client_reply_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  resolved_at: z.string().nullable().optional(),
});
export type SupportTicketDto = z.infer<typeof SupportTicketDtoSchema>;

// Empty body — caller is identified by session, ticket id is in the path.
export const SupportClaimInputSchema = z.object({}).strict();
export type SupportClaimInput = z.infer<typeof SupportClaimInputSchema>;

// Manual status transition by staff. Server validates against the state
// machine (see PLAN §4) and returns HttpError(409, 'invalid_transition') on
// illegal moves.
export const SupportStatusTransitionInputSchema = z.object({
  status: SupportTicketStatusSchema,
});
export type SupportStatusTransitionInput = z.infer<typeof SupportStatusTransitionInputSchema>;

// ── Support reply thread (Phase B) ────────────────────────────────────────
// One row per reply on a ticket. Stored in a sibling collection
// `support_message` (NOT embedded) so the thread can grow without rewriting
// the ticket doc; matches the precedent set by `models/message.ts`.
//
// `author_name` and `client_id` are denormalised onto each row so the UI
// can render the thread without joining UserModel / SupportTicketModel.
export const SupportMessageDtoSchema = z.object({
  id: z.string(),                       // uuid
  ticket_id: z.string(),                // SUP-XXXX
  author_id: z.string(),
  author_name: z.string(),
  author_kind: z.enum(['client', 'staff']),
  body: z.string(),
  created_at: z.string(),
});
export type SupportMessageDto = z.infer<typeof SupportMessageDtoSchema>;

// Client → server when posting a reply. Body length matches the ticket
// body's 5000-char ceiling so a reply can quote the original if needed.
export const SupportMessageInputSchema = z.object({
  body: z.string().min(1, 'Reply cannot be empty').max(5000, 'Reply is too long (max 5000 characters)'),
});
export type SupportMessageInput = z.infer<typeof SupportMessageInputSchema>;

export const SUPPORT_CATEGORY_LABEL: Record<SupportCategory, string> = {
  account:    'Account / sign-in',
  billing:    'Billing / invoice',
  engagement: 'Engagement / brief',
  technical:  'Technical / portal',
  other:      'Other',
};

// Status label is perspective-sensitive — `awaiting_client` reads as
// "Awaiting client" to a staff user and "Awaiting your reply" to a client.
// All other statuses render identically on both sides.
export function getStatusLabel(
  status: SupportTicketStatus,
  perspective: 'staff' | 'client',
): string {
  switch (status) {
    case 'open':            return 'Open';
    case 'acknowledged':    return 'Acknowledged';
    case 'in_progress':     return 'In progress';
    case 'awaiting_client': return perspective === 'staff' ? 'Awaiting client' : 'Awaiting your reply';
    case 'resolved':        return 'Resolved';
    case 'closed':          return 'Closed';
  }
}
